import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PeoplePicker, useAbsenceMap } from "@/components/shared/PeoplePicker";
import { api, formatApiError } from "@/lib/api";
import { plur } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Minus, X, Check, Copy } from "lucide-react";

// 409: parta v deníku — stavbyvedoucí vybere lidi z týmu zakázky (třeba ti, kdo aplikaci nemají),
// napíše hodiny čisté práce a přestávku (auto podle pravidla firmy, nebo ručně) a „Uložit do docházky“
// zapíše schválené směny. Kdo má ten den vlastní docházku / výkaz, vybrat nejde (hodiny by byly dvakrát).
const BRKS = [0, 15, 20, 30, 45, 60];
export const crewAutoBreak = (rule, h) => (h > (rule?.min_hours ?? 6) ? (rule?.minutes ?? 30) : 0);
const num = (v) => Number(String(v ?? "").replace(",", "."));
export const fmtH = (h) => String(Math.round(Number(h || 0) * 100) / 100).replace(".", ",");
const pad = (n) => String(n).padStart(2, "0");
const toHHMM = (v) => { const m = /^(\d{1,2}):(\d{2})/.exec(v || ""); return m ? `${pad(+m[1])}:${m[2]}` : "07:00"; };
export function crewEnd(start, hours, brk) {
  const m = /^(\d{1,2}):(\d{2})/.exec(start || "");
  if (!m || !(num(hours) > 0)) return "–";
  const t = +m[1] * 60 + +m[2] + Math.round(num(hours) * 60) + (brk || 0);
  return `${Math.floor(t / 60) % 24}:${pad(t % 60)}`;
}
const brkOf = (rule, r) => (r.brk == null ? crewAutoBreak(rule, num(r.hours)) : r.brk);

function BreakSelect({ rule, value, hours, onChange, testid, small }) {
  return (
    <select value={value == null ? "" : String(value)} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      aria-label="Přestávka" data-testid={testid}
      className={`rounded-[10px] border bg-card px-2 tabular-nums ${small ? "h-9 text-[13px]" : "h-10 text-[14px]"} ${value == null ? "border-border text-muted-foreground" : "border-brand bg-brand/10 text-foreground"}`}>
      <option value="">auto · {crewAutoBreak(rule, num(hours))} min</option>
      {BRKS.map((m) => <option key={m} value={m}>{m} min</option>)}
    </select>
  );
}

// 411: začátek jako stejný rozbalovací výběr jako přestávka (po 15 min, 5:00–20:00). Sloupcový výběr
// času se uvnitř tabulky ořízl a při posouvání se zasekával na 00 / 55.
const TIMES = Array.from({ length: (20 - 5) * 4 + 1 }, (_, k) => { const m = 5 * 60 + k * 15; return `${pad(Math.floor(m / 60))}:${pad(m % 60)}`; });
function TimeSelect({ value, onChange, label, testid, small, edited }) {
  const v = toHHMM(value);
  const opts = TIMES.includes(v) ? TIMES : [...TIMES, v].sort();
  return (
    <select value={v} onChange={(e) => onChange(e.target.value)} aria-label={label} data-testid={testid}
      className={`rounded-[10px] border bg-card px-2 tabular-nums ${small ? "h-9 text-[13px]" : "h-10 text-[14px]"} ${edited ? "border-brand bg-brand/10" : "border-border"}`}>
      {opts.map((t) => <option key={t} value={t}>{t}</option>)}
    </select>
  );
}

const fromServer = (rows) => (rows || []).map((r) => ({ user_id: r.user_id, name: r.name, position: r.position, start: toHHMM(r.start),
  hours: fmtH(r.hours), brk: r.break_auto ? null : r.break_minutes }));

const sig = (rs) => JSON.stringify(rs.map((r) => [r.user_id, toHHMM(r.start), num(r.hours), r.brk ?? null]));

// 410: výběr z týmu otvírá rozbalovací „Přidat lidi“ v hlavičce karty (ref.openPick)
export const DiaryCrew = forwardRef(function DiaryCrew({ projectId, date, onSaved, onRows, onCount }, ref) {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [all, setAll] = useState({ start: "07:00", hours: "8", brk: null });
  const [saving, setSaving] = useState(false);
  const [pick, setPick] = useState(false);
  const [sel, setSel] = useState([]);
  const absences = useAbsenceMap(date, date, pick);

  const apply = useCallback((d) => { setData(d); const r = fromServer(d.rows); setRows(r); onRows?.(r); }, [onRows]);
  useEffect(() => {
    let live = true;
    setData(null); setRows([]);
    api.get(`/projects/${projectId}/diary/${date}/crew`).then((r) => live && apply(r.data)).catch(() => live && setData({ team: [], rows: [], rule: null }));
    return () => { live = false; };
  }, [projectId, date, apply]);

  useEffect(() => { onCount?.(rows.length); }, [rows.length, onCount]);
  // 410: neuloženo = jiná parta než v docházce (dřív stačilo otevřít a zavřít výběr → „0 lidí · Uložit“)
  const dirty = !!data && sig(rows) !== sig(fromServer(data.rows));
  const rule = data?.rule;
  const free = useMemo(() => (data?.team || []).filter((t) => !t.own), [data]);
  const own = useMemo(() => (data?.team || []).filter((t) => t.own), [data]);
  const total = rows.reduce((s, r) => s + (num(r.hours) || 0), 0);
  const change = (i, patch) => setRows((rs) => rs.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  const remove = (i) => setRows((rs) => rs.filter((_, k) => k !== i));
  const step = (i, d) => change(i, { hours: fmtH(Math.min(16, Math.max(0.5, (num(rows[i].hours) || 0) + d))), edH: true });
  const applyAll = () => {
    setRows((rs) => rs.map((r) => ({ ...r, start: all.start, hours: all.hours, brk: all.brk, edH: false, edS: false })));
    toast.success(`Všem: ${all.hours} h od ${all.start}${all.brk == null ? "" : `, přestávka ${all.brk} min`}`);
  };
  const openPick = () => { setSel(rows.map((r) => r.user_id)); setPick(true); };
  useImperativeHandle(ref, () => ({ openPick }));
  const confirmPick = () => {
    const keep = rows.filter((r) => sel.includes(r.user_id));
    const add = sel.filter((id) => !rows.some((r) => r.user_id === id)).map((id) => {
      const t = free.find((x) => x.id === id) || {};
      return { user_id: id, name: t.name, position: t.position, start: all.start, hours: all.hours, brk: all.brk };
    });
    setRows([...keep, ...add].sort((a, b) => (a.name || "").localeCompare(b.name || "", "cs"))); setPick(false);
  };
  const save = async () => {
    const bad = rows.find((r) => !(num(r.hours) >= 0.25 && num(r.hours) <= 16));
    if (bad) return toast.error(`${bad.name}: hodiny musí být 0,25–16.`);
    setSaving(true);
    try {
      const { data: d } = await api.put(`/projects/${projectId}/diary/${date}/crew`, {
        rows: rows.map((r) => ({ user_id: r.user_id, start: r.start, hours: num(r.hours), break_minutes: r.brk })) });
      apply(d);
      toast.success(d.rows.length ? `Zapsáno do docházky · ${plur(d.rows.length, "člověk", "lidé", "lidí")}` : "Parta odebrána z docházky");
      onSaved?.(fromServer(d.rows));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!data) return null;
  return (
    <div className="space-y-2" data-testid="diary-crew">
      {rows.length > 0 && (
        <>
          {/* Všem stejně — vyplní se jednou a „Nastavit všem“ ho zkopíruje do všech řádků */}
          <div className="flex flex-wrap items-center gap-2 rounded-[14px] bg-secondary/60 p-2.5" data-testid="diary-crew-all">
            <span className="text-sm font-bold mr-1">Všem stejně</span>
            <TimeSelect value={all.start} onChange={(v) => setAll({ ...all, start: v })} label="Začátek všem" testid="diary-crew-all-start" />
            <span className="inline-flex items-center gap-1">
              <input inputMode="decimal" value={all.hours} onChange={(e) => setAll({ ...all, hours: e.target.value })} aria-label="Hodiny všem"
                className="h-10 w-[64px] rounded-[10px] border border-border bg-card text-center text-[15px] font-semibold tabular-nums" data-testid="diary-crew-all-hours" />
              <span className="text-xs text-muted-foreground">h</span>
            </span>
            <BreakSelect rule={rule} value={all.brk} hours={all.hours} onChange={(v) => setAll({ ...all, brk: v })} testid="diary-crew-all-break" />
            <Button variant="outline" size="sm" onClick={applyAll} className="md:ml-auto" data-testid="diary-crew-apply-all"><Copy size={14} /> Nastavit všem</Button>
          </div>

          {/* PC: tabulka */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm tabular-nums">
              <thead><tr className="text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border">
                <th className="text-left font-bold py-2 pr-2">Pracovník</th><th className="text-left font-bold py-2 px-2">Od</th>
                <th className="text-left font-bold py-2 px-2">Hodiny</th><th className="text-left font-bold py-2 px-2">Přestávka</th>
                <th className="text-left font-bold py-2 px-2">Do</th><th className="py-2" />
              </tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.user_id} className="border-b border-border/70 last:border-0" data-testid={`diary-crew-row-${i}`}>
                    <td className="py-2 pr-2 min-w-0"><div className="font-semibold truncate max-w-[220px]">{r.name}</div>
                      {r.position && <div className="text-xs text-muted-foreground truncate max-w-[220px]">{r.position}</div>}</td>
                    <td className="py-2 px-2"><TimeSelect value={r.start} onChange={(v) => change(i, { start: v, edS: true })} label={`Začátek ${r.name}`}
                      edited={r.edS} testid={`diary-crew-start-${i}`} /></td>
                    <td className="py-2 px-2"><input inputMode="decimal" value={r.hours} onChange={(e) => change(i, { hours: e.target.value, edH: true })} aria-label={`Hodiny ${r.name}`}
                      className={`h-10 w-[64px] rounded-[10px] border text-center font-semibold bg-card ${r.edH ? "border-brand bg-brand/10" : "border-border"}`} data-testid={`diary-crew-hours-${i}`} /></td>
                    <td className="py-2 px-2"><BreakSelect rule={rule} value={r.brk} hours={r.hours} onChange={(v) => change(i, { brk: v })} testid={`diary-crew-break-${i}`} /></td>
                    <td className="py-2 px-2 font-semibold" data-testid={`diary-crew-end-${i}`}>{crewEnd(r.start, r.hours, brkOf(rule, r))}</td>
                    <td className="py-2 text-right"><button type="button" onClick={() => remove(i)} aria-label={`Odebrat ${r.name}`}
                      className="h-9 w-9 rounded-[10px] text-muted-foreground hover:text-destructive" data-testid={`diary-crew-remove-${i}`}><X size={16} className="mx-auto" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Telefon: řádky s − / + po půl hodině */}
          <div className="md:hidden divide-y divide-border">
            {rows.map((r, i) => (
              <div key={r.user_id} className="py-2.5 grid gap-1.5" data-testid={`diary-crew-mrow-${i}`}>
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0 font-semibold truncate">{r.name}</div>
                  <div className="inline-flex items-center rounded-[12px] border border-border overflow-hidden shrink-0">
                    <button type="button" onClick={() => step(i, -0.5)} aria-label="Méně" className="h-11 w-10 bg-secondary grid place-items-center"><Minus size={16} /></button>
                    <span className={`w-14 text-center font-bold tabular-nums ${r.edH ? "text-brand" : ""}`} data-testid={`diary-crew-mhours-${i}`}>{r.hours} h</span>
                    <button type="button" onClick={() => step(i, 0.5)} aria-label="Více" className="h-11 w-10 bg-secondary grid place-items-center"><Plus size={16} /></button>
                  </div>
                  <button type="button" onClick={() => remove(i)} aria-label={`Odebrat ${r.name}`} className="h-11 w-8 text-muted-foreground shrink-0"><X size={16} className="mx-auto" /></button>
                </div>
                <div className="flex items-center gap-1.5">
                  <TimeSelect small value={r.start} onChange={(v) => change(i, { start: v, edS: true })} label={`Začátek ${r.name}`} edited={r.edS} />
                  <span className="text-sm text-muted-foreground tabular-nums whitespace-nowrap">– {crewEnd(r.start, r.hours, brkOf(rule, r))}</span>
                  <span className="ml-auto"><BreakSelect small rule={rule} value={r.brk} hours={r.hours} onChange={(v) => change(i, { brk: v })} /></span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {(rows.length > 0 || dirty) && (
        <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="diary-crew-foot">
          <span className="text-sm md:ml-auto">{rows.length > 0 && <b>{plur(rows.length, "člověk", "lidé", "lidí")} · {fmtH(total)} h · </b>}
            <span className="text-muted-foreground">{!dirty ? "v docházce, schváleno" : rows.length ? "uloží se do docházky jako schválené" : "parta se odebere z docházky"}</span></span>
          <Button size="sm" onClick={save} disabled={!dirty || saving} className="w-full md:w-auto bg-success hover:bg-success/90 text-white" data-testid="diary-crew-save">
            <Check size={15} /> {saving ? "Ukládám…" : !dirty ? "Uloženo" : rows.length ? "Uložit do docházky" : "Odebrat z docházky"}
          </Button>
        </div>
      )}

      <Dialog open={pick} onOpenChange={setPick}>
        <DialogContent sheet onOpenAutoFocus={(e) => e.preventDefault()} className="max-w-[480px] md:max-h-[85vh] overflow-y-auto max-md:pt-8" data-testid="diary-crew-dialog">
          <DialogHeader><DialogTitle>Kdo dnes byl na stavbě?</DialogTitle>
            <p className="text-sm text-muted-foreground">Lidé z týmu zakázky. Hodiny jim napíšete v deníku.</p></DialogHeader>
          {free.length ? (
            <PeoplePicker people={free} selected={sel} absences={absences} testid="diary-crew-people" maxH="max-h-[46vh]"
              onToggle={(id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))} sub={(p) => p.position || null} />
          ) : <p className="text-sm text-muted-foreground border border-border rounded-[14px] p-3">V týmu zakázky není nikdo k výběru. Přidejte lidi v záložce Tým.</p>}
          {own.length > 0 && (
            <p className="text-[12.5px] text-muted-foreground" data-testid="diary-crew-own">
              Vlastní docházku dnes mají (hodiny si zapíšou sami): {own.map((t) => `${t.name} (${/^\d/.test(t.own) ? `od ${t.own}` : t.own})`).join(", ")}
            </p>
          )}
          <div className="flex flex-col-reverse md:flex-row gap-2 md:justify-end">
            <Button variant="outline" onClick={() => setSel(free.map((t) => t.id))} disabled={!free.length} className="h-11 w-full md:w-auto">Vybrat všechny</Button>
            <Button onClick={confirmPick} className="h-11 w-full md:w-auto" data-testid="diary-crew-pick-ok">
              {sel.length ? `Hotovo · ${plur(sel.length, "člověk", "lidé", "lidí")}` : "Hotovo"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
});
